import _, { isEmpty } from 'lodash';
import moment from 'moment';
import { differenceInDays } from 'date-fns';
import Constants from './Constants';
import { convertToCamelCase, checkForAsianGrinder, checkForUSAGrinder } from './helpers';
import { isStartDate, isValidEndDate } from '@/components/core/DatePicker';

/**
 * - getIngredientOptions(input_products, filterOptions) returns a list of ingredients
 *    based on the filters passed as arguments.
 *    By default filters out delisted
 * - filterOptions = [
 *                  {filter_key: "COUNTRY", filter_args: "[US]"},
 *                  {}, ..
 *                ]
 * - filterTypes is a dict with filter keys and functions
 */
function filterIngredientOptionsByCountry(ingredientsOptions, inputCountries) {
  if (!inputCountries) {
    return [];
  }
  const lowerCaseCountries = inputCountries.map(x => x?.toLowerCase());
  const countryCodes = Constants.COUNTRY_CODINGS.filter(x => lowerCaseCountries.includes(x?.name)).map(
    ({ code }) => code
  );
  if (!countryCodes) {
    return [];
  }
  const options = ingredientsOptions?.filter(c => countryCodes.includes(c.country));
  return options;
}

/**
 * Filters products based on grinder destination country for AU/NZ orders
 * Scenario 1: If grinder country is China, only show China-only products
 * Scenario 2: If grinder country is NOT China (and in the restricted list), exclude China-only products
 * 
 * @param {Array} ingredientsOptions - The products to filter
 * @param {String} grinderDestinationCountry - The grinder's destination country
 * @returns {Array} - Filtered products
 */
function filterIngredientOptionsByGrinderCountry(ingredientsOptions, grinderDestinationCountry) {
  if (!grinderDestinationCountry || !ingredientsOptions) {
    return ingredientsOptions || [];
  }

  const grinderCountryLower = grinderDestinationCountry.toLowerCase();
  const isChinaGrinder = grinderCountryLower === Constants.COUNTRIES.CHINA;
  const isRestrictedGrinder = Constants.NON_CHINA_GRINDER_COUNTRIES.some(
    country => country.toLowerCase() === grinderCountryLower
  );

  // Scenario 1: China grinder - ONLY show China-only products
  if (isChinaGrinder) {
    return ingredientsOptions.filter(product => 
      Constants.CHINA_ONLY_PRODUCT_UIDS.includes(product.uid)
    );
  }

  // Scenario 2: Non-China grinder in restricted list - EXCLUDE China-only products
  if (isRestrictedGrinder) {
    return ingredientsOptions.filter(product => 
      !Constants.CHINA_ONLY_PRODUCT_UIDS.includes(product.uid)
    );
  }

  // For all other grinders (not China, not in restricted list) - show all products
  return ingredientsOptions;
}

const filterTypes = { 
  COUNTRY: filterIngredientOptionsByCountry,
  GRINDER_COUNTRY: filterIngredientOptionsByGrinderCountry,
};

export function getIngredientOptions(inputProducts, filterOptions, skipDelisted = false) {
  let newInputProducts = inputProducts;
  if (filterOptions.length) {
    Object.entries(filterOptions).forEach(opt => {
      const key = opt[1].filter_key || opt[1].filterKey;
      const args = opt[1].filter_args || opt[1].filterArgs;
      newInputProducts = filterTypes[key].apply(null, [inputProducts, args]);
    });
  }

  return skipDelisted
    ? newInputProducts?.map(el => ({ value: el.uid, label: el.name, delisted: el.delisted, cl: el.cl }))
    : newInputProducts
        ?.filter(product => !product.delisted)
        .map(el => ({ value: el.uid, label: el.name, delisted: el.delisted, cl: el.cl }));
}

/* -- getIngredientOptions end-- */

export function filterInputProducts(inputProducts, form) {
  let filteredInputProducts = inputProducts.filter(product => (form.isChilled ? product.fresh : !product.fresh));
  if (form.productType) {
    // filter by product type
    filteredInputProducts = filteredInputProducts.filter(product => product.productType === form.productType);
  }

  return filteredInputProducts;
}

/* -- today's date -- */

export function today() {
  return moment().format('YYYY-MM-DD');
}

// validates shipment dates
export function shipmentDateValidations(title, fieldName, inputDate, state,grinderCountry,loadContainerDetails,orderDate) {
  // BOP-7950 - Shipment date - to be backdated
  // if (inputDate < today()) {
  //   return `${title} cannot be in the past.`;
  // }

  const isFmgTenant = getTenantFromDomain() === 'fmg';
  const hasAsianGrinder = isFmgTenant && checkForAsianGrinder(grinderCountry?.toLowerCase());
  const hasUSAGrinder = isFmgTenant && checkForUSAGrinder(grinderCountry?.toLowerCase());

  const restrictShipmentDates = isShipmentWindowLocked(grinderCountry, loadContainerDetails, orderDate);
  
  if (state) {
    switch (fieldName) {
      case 'shipmentDateStart':
        // start date > end date
        if (state.shipmentDateEnd && inputDate > state.shipmentDateEnd)
          return `${title} cannot be after Shipment End Date`;
        if (restrictShipmentDates && !isStartDate(inputDate, true)) {
          return `${title} must be the 1st or 16th of the month`;
        }

        if(restrictShipmentDates && hasAsianGrinder){
          const days = differenceInDays(new Date(state.shipmentDateEnd), new Date(inputDate));
          if(days > 31){
              return 'For Asian Grinders Shipment Start Date & End Date Must Not Exceed 31 Days';
            }
        }
          if(restrictShipmentDates  && hasUSAGrinder){
          const days = differenceInDays(new Date(state.shipmentDateEnd), new Date(inputDate));
          if(days > 31){
              return 'For US Grinders Shipment Start Date & End Date Must Not Exceed 31 Days';
            }
        }
        break;
      case 'shipmentDateEnd':
        // end date < start date
        if (state.shipmentDateStart && inputDate < state.shipmentDateStart)
          return `${title} cannot be before Shipment Start Date`;

        if (restrictShipmentDates && !isValidEndDate(inputDate, true)) {
          return `${title} must be the 15th or the last day of the month`;
        }

        if(restrictShipmentDates && hasAsianGrinder){
          const days = differenceInDays( new Date(inputDate),new Date(state.shipmentDateStart));
          if(days > 31){
              return 'For Asian Grinders Shipment Start Date & End Date Must Not Exceed 31 Days';
            }
        }
          if(restrictShipmentDates && hasUSAGrinder){
          const days = differenceInDays(new Date(inputDate),new Date(state.shipmentDateStart));
          if(days > 31){
              return 'For US Grinders Shipment Start Date & End Date Must Not Exceed 31 Days';
            }
        }
        break;
      case 'arrivalDateStart':
        // start date > end date
        if (state.arrivalDateEnd && inputDate > state.arrivalDateEnd)
          return `${title} cannot be after Arrival End Date`;
        break;
      case 'arrivalDateEnd':
        // end date < start date
        if (state.arrivalDateStart && inputDate < state.arrivalDateStart)
          return `${title} cannot be before Arrival Start Date`;
        break;
      default:
        return null;
    }
  }
  return null;
}

// validation for expected production dates and production details (shipped_quantity, production dates)

export function productionDateValidations(title, fieldName, inputDate, state) {
  if (state) {
    switch (fieldName) {
      case 'shipment_date_end':
        // end date < any line's expected fabrication date
        if (inputDate && state.lines) {
          try {
            const errorLines = state.lines.find(line => inputDate < line.expected_production_date);
            if (errorLines) {
              return `${title} cannot be before Expected production date ${errorLines.expected_production_date} on line ${errorLines.idx}`;
            }
          } catch (e) {
            // TODO, log errors
            return null;
          }
        }
        break;
      default:
        return null;
    }
  }
  return null;
}

export function formatLinkToDisplay(url) {
  const urlArray = url.split('/');
  return urlArray[urlArray.length - 1];
}

// compare two arrays and igore the order
export function equalsIgnoreOrder(a, b) {
  if (a.length !== b.length) return false;
  const uniqueValues = new Set([...a, ...b]);
  // eslint-disable-next-line no-restricted-syntax
  for (const v of uniqueValues) {
    const aCount = a.filter(e => e === v).length;
    const bCount = b.filter(e => e === v).length;
    if (aCount !== bCount) return false;
  }
  return true;
}

export function getLevelName(name) {
  if (typeof name === 'function') {
    return name().replace('null', '');
  }
  return name;
}

export function getInternalPoLabel(arr) {
  const configs = convertToCamelCase(arr);
  const obj = configs.find(o => o.name === 'internal_po_number');
  if (obj) {
    return obj.valueStr;
  }
  return '';
}
export function getDefaultValueFromConfigs(arr, name) {
  const configs = convertToCamelCase(arr);
  const obj = configs.find(o => o.name === name);
  if (obj) {
    return obj.valueStr || obj.valueInt;
  }
  return '';
}

export function isManualPriceStrategyByEndUser(arr, endUserId) {
  const endUsers = convertToCamelCase(arr);
  const endUser = endUsers.find(u => u.id === endUserId);
  if (endUser) {
    if (endUser.pricingStrategy === Constants.PRICING_STRATEGY.MANUAL) {
      return true;
    }
  }
  return false;
}

export function getPricingStrategy(arr) {
  const configs = convertToCamelCase(arr);
  const obj = configs.find(o => o.name === 'pricing_strategy');
  if (!obj) {
    return '';
  }
  return obj.valueStr;
}

export function isCombinePriceStrategy(arr) {
  return getPricingStrategy(arr) === Constants.PRICING_STRATEGY.COMBINE;
}

export function isManualPriceStrategy(arr) {
  return getPricingStrategy(arr) === Constants.PRICING_STRATEGY.MANUAL;
}

export function isManualPriceStrategyByConfigsAndEndUser(configs, endUsers, endUserId) {
  const pricingStrategy = getPricingStrategy(configs);
  if (pricingStrategy === Constants.PRICING_STRATEGY.DEFAULT) {
    return false;
  }
  if (pricingStrategy === Constants.PRICING_STRATEGY.MANUAL) {
    return true;
  }
  return isManualPriceStrategyByEndUser(endUsers, endUserId);
}

export function checkIfManualSurchargesApply(configsArray) {
  const configs = convertToCamelCase(configsArray);
  const manualSurchargeObj = configs.find(c => c.name === 'manual_fees');
  if (manualSurchargeObj) {
    return manualSurchargeObj.valueStr === 'on';
  }
  return false;
}

export function isDeliveryDateEditable(arr) {
  const configs = convertToCamelCase(arr);
  const obj = configs.find(o => o.name === 'actual_delivery_date');
  if (obj) {
    return obj.valueInt === 1;
  }

  return false;
}
export function checkIfETDEditable(docs) {
  const obj = docs.find(doc => doc.docType === 'Grinder Copy Documents' && doc.id);

  return isEmpty(obj);
}

export function isPoHedgingEnabled(arr) {
  const configs = convertToCamelCase(arr);
  const obj = configs.find(o => o.name === 'po_hedging');
  if (obj) {
    return obj.valueStr === 'on';
  }
  return false;
}

export function isInvoiceColdstoreValidationApplied(arr) {
  const configs = convertToCamelCase(arr);
  const obj = configs.find(o => o.name === 'invoice_coldstore_validation_applied');
  if (obj) {
    return obj.valueStr === '1';
  }
  return false;
}

export function isQuantityReceivedEditable(arr) {
  const configs = convertToCamelCase(arr);
  const obj = configs.find(o => o.name === 'quantity_received');
  if (obj) {
    return obj.valueInt === 1;
  }
  return false;
}

export function showCourierTrackingNo(arr) {
  const configs = convertToCamelCase(arr);
  const obj = configs.find(o => o.name === 'show_courier_tracking_no');
  if (obj) {
    return obj.valueInt === 1;
  }
  return false;
}

export function showIsfFileDate(arr) {
  const configs = convertToCamelCase(arr);
  const obj = configs.find(o => o.name === 'show_isf_file_date');
  if (obj) {
    return obj.valueInt === 1;
  }
  return false;
}

export function showActualDateOfArrival(arr) {
  const configs = convertToCamelCase(arr);
  const obj = configs.find(o => o.name === 'show_actual_date_of_arrival');
  if (obj) {
    return obj.valueInt === 1;
  }
  return false;
}

export function showActualDateOfDeparture(arr) {
  const configs = convertToCamelCase(arr);
  const obj = configs.find(o => o.name === 'show_actual_date_of_departure');
  if (obj) {
    return obj.valueInt === 1;
  }
  return false;
}

export function showUsCustomsEntryNumber(arr) {
  const configs = convertToCamelCase(arr);
  const obj = configs.find(o => o.name === 'show_us_customs_entry_number');
  if (obj) {
    return obj.valueInt === 1;
  }
  return false;
}

export function showDocsSentToBank(arr) {
  const configs = convertToCamelCase(arr);
  const obj = configs.find(o => o.name === 'show_docs_sent_to_bank');
  if (obj) {
    return obj.valueInt === 1;
  }
  return false;
}

export function getDocsSentToCustomFieldName(arr) {
  const configs = convertToCamelCase(arr);
  const obj = configs.find(o => o.name === 'docs_sent_to_customer_field_name');
  if (obj) {
    return obj.valueStr;
  }
  return 'Docs sent to customs broker';
}

export function getOptionsFromSettingsConfig(arr, name, subName, useKeyAsValueAndLabel = false) {
  let obj = arr.find(o => o.name === name)?.value;
  const options = [];

  if (obj) {
    if (subName) {
      obj = obj[subName] || {};
    }
    Object.keys(obj).forEach(k => {
      if (useKeyAsValueAndLabel) {
        options.push({ value: k, label: k });
      } else {
        options.push({ value: obj[k], label: obj[k] });
      }
    });
  }
  return options;
}

export function IsValidUSDomesticOrder(arr) {
  const configs = convertToCamelCase(arr);
  const obj = configs.find(o => o.name === 'us_domestic_validation');
  if (obj) {
    return obj.valueInt === 1;
  }
  return false;
}

export function isFinanceTableRowsValidationIgnored(arr) {
  const configs = convertToCamelCase(arr);
  const obj = configs.find(o => o.name === 'finance_validation_ignored');
  if (obj) {
    return obj.valueInt === 1;
  }
  return false;
}

export function showLiveClSection(arr) {
  const configs = convertToCamelCase(arr);
  const obj = configs.find(o => o.name === 'show_live_cl');
  if (obj) {
    return obj.valueInt === 1;
  }
  return false;
}

export function getBuyActivityGroupByValueFromConfigs(arr) {
  const configs = convertToCamelCase(arr);
  const obj = configs.find(o => o.name === 'buy_activity_query');
  if (obj) {
    return obj.valueStr;
  }
  return 'cl';
}

export function changeableKeyCreator(keys) {
  // creator
  let value = '';
  for (const key of keys) {
    value += JSON.stringify(key);
  }
  return value;
}

export function checkIfIsPackerUser(user) {
  const organisationFilter = user.organisation_filter || user.organisationFilter;
  if (organisationFilter) {
    return organisationFilter.entity === 'packer' || organisationFilter.entity === 'packer group';
  }
  return false;
}

export function checkIfIsGrinderUser(user) {
  const organisationFilter = user.organisation_filter || user.organisationFilter;
  if (organisationFilter) {
    return organisationFilter.entity === 'grinder' || organisationFilter.entity === 'grinder group';
  }
  return false;
}

export function checkIfIsAdminUser(user) {
  return !checkIfIsPackerUser(user) && !checkIfIsGrinderUser(user);
}
export function isDeliveryDateRequired(arr) {
  const configs = convertToCamelCase(arr);
  const obj = configs.find(o => o.name === 'required_delivery_date');
  if (obj) {
    return obj.valueInt === 1;
  }
  return false;
}

export const downloadFile = async (fileUrl, name = 'Document.pdf') => {
  try {
    const response = await fetch(fileUrl);

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = decodeURI(name);
    link.dispatchEvent(new MouseEvent('click'));
  } catch (error) {
    console.error('Error downloading file', error);
  }
};

/**
 * Checks if a specific feature is enabled based on its configuration value.
 * 
 * This function looks up the feature name in the provided configuration array (usually retrieved from the Master Data Management system)
 * and checks if its corresponding value (`valueInt`) is set to 1, which indicates that the feature is enabled.
 * 
 * The purpose of this function is to enable or disable specific content or behavior on the application based on 
 * the configuration settings stored in the Master Data Management -> Config.
 * 
 * @param {Array} arr - The array of config objects, typically retrieved from the Master Data Management system->config.
 * @param {string} featureName - The name of the feature to check (e.g., 'show_us_customs_entry_number', 'show_docs_sent_to_bank').
 * @returns {boolean} - Returns `true` if the feature is enabled (i.e., `valueInt === 1`), otherwise returns `false`.
 */
export function isFeatureEnabled(arr, featureName) {
  const configs = convertToCamelCase(arr);
  const obj = configs.find(o => o.name === featureName);
  if (obj) {
    return obj.valueInt === 1;// Return true if feature is enabled
  }
  return false; // Return false if the feature is not found or is disabled
}

export function validatePassword(password = '', email = '', isOptional = false) {
  const { MIN: minLength, MAX: maxLength, SPECIAL_CHARS: specialCharacters, FORBIDDEN_CHARS: forbiddenCharacters } = Constants.PASSWORD_VALIDATION;
  if (isOptional && (!password || password.trim() === "")) {
    return null;  
  }
  if (!isOptional && (!password || password.trim() === "")) {
    return 'Password is required.';
  }
  if (password.length < minLength || password.length > maxLength) {
    return `Password must be between ${minLength} and ${maxLength} characters long.`;
  }
  if (email && password.toLowerCase().includes(email.toLowerCase())) {
    return "Password must not contain your email address.";
  }
  const validations = [
    { regex: /[A-Z]/, message: "Password must contain at least one uppercase letter (A-Z)." },
    { regex: /[a-z]/, message: "Password must contain at least one lowercase letter (a-z)." },
    { regex: /[0-9]/, message: "Password must contain at least one number (0-9)." },
    { regex: new RegExp(`[${specialCharacters}]`), message: `Password must contain at least one special character from the allowed set: ${specialCharacters}` },
    { regex: new RegExp(`[${forbiddenCharacters}]`), message: "Password must not contain single (') or double (\") quotes.", negate: true }
  ];
  const errorMessage = validations.find(({ regex, negate }) => (negate ? regex.test(password) : !regex.test(password)))?.message;
  return errorMessage || null;
}


export const formatNumber = (num, decimalPlaces = 4) => {
  if (!_.isFinite(num)) return num; // handle NaN, null, 
  return _.isInteger(num) ? num : num.toFixed(decimalPlaces);
};

export function getTenantFromDomain() {
  const hostname = window.location.hostname.toLowerCase();
  if (hostname.includes('cfc')) return 'cfc';
  return 'fmg';
}


export const SHIPMENTWINDOW_LOCK_CUTOFF_DATE = '2026-10-02';

export function isPurchaseOrderSubjectToShipmentWindowLock(orderDate) {
  if (!orderDate) return true;
  const orderCalendarDate = String(orderDate).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(orderCalendarDate)) return true;
  return orderCalendarDate >= SHIPMENTWINDOW_LOCK_CUTOFF_DATE;
}

export function isShipmentWindowLocked(grinderDestinationCountry, loadContainerDetails, orderDate) {
  const hasAsianGrinder = checkForAsianGrinder(grinderDestinationCountry?.toLowerCase());
  const hasUSAGrinder = checkForUSAGrinder(grinderDestinationCountry?.toLowerCase());
  const isContainerisedLoad = !!loadContainerDetails && String(loadContainerDetails).toLowerCase() !== 'truck';
  return (
    getTenantFromDomain() === 'fmg' &&
    (hasAsianGrinder || hasUSAGrinder) &&
    isContainerisedLoad &&
    isPurchaseOrderSubjectToShipmentWindowLock(orderDate)
  );
}

export function getFontFamilyForTenant() {
  const tenant = getTenantFromDomain();
  return tenant.includes('cfc') ? 'Poppins' : 'Roboto, sans-serif';
}

export function getColourForTenant() {
  const tenant = getTenantFromDomain();
  return tenant.includes('cfc') ? '#0F1B48' : '#031b4e';
}

export function applyTenantClassCustom() {
  const hostname = window.location.hostname.toLowerCase();
  const tenant = hostname.includes('cfc') ? 'cfc' : 'fmg';
  document.body.classList.add(`tenant-${tenant}`);
}
